import React, { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { ProductKeys } from '../../../../../common/constants/product-keys';
import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import DateFilterWithCustomRangePicker from '../../../../../common/ui/filters/last-modified-filter';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { usePrimaryProduct } from '../../../../../controllers/store/bootstrap';
import { useSelectedProduct } from '../../../../../controllers/store/search';
import { filter as lastUpdatedFilter } from '../../../../../controllers/store/search/filters/last-updated';

import { i18n } from './messages';

const LastUpdatedFilter = ({
	testId = 'universal-last-updated-filter',
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
}: {
	testId?: string;
} & FilterProps): React.JSX.Element | null => {
	const [primaryProduct] = usePrimaryProduct();
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	// Trello only supports day/week/month/year options
	const trelloExcludedOptions = useMemo(() => ['custom', 'yesterday'], []);
	const optionsToExclude = selectedProduct === ProductKeys.Trello ? trelloExcludedOptions : [];

	const [filterData, { setOption, clearOptions, loadInitialOptions }] = useSelectFilter({
		filterId: lastUpdatedFilter.id,
		optionValuesToExclude: optionsToExclude,
	});

	// This is a workaround to clear the options when the product is change to Trello
	// To avoid Trello query get filtered by un applicable options that might confuse the user
	// If Trello later served as indexed data that support all date options, this can be removed
	useEffect(() => {
		const isTrelloUnapplicableOptionsSelected =
			filterData?.selectedOptions &&
			filterData?.selectedOptions.filter((option) => trelloExcludedOptions.includes(option.value))
				.length > 0;
		if (selectedProduct === ProductKeys.Trello && isTrelloUnapplicableOptionsSelected) {
			clearOptions();
		}
	}, [clearOptions, filterData?.selectedOptions, selectedProduct, trelloExcludedOptions]);

	if (!filterData) {
		return null;
	}

	const { options, selectedOptions, customValue } = filterData;

	const label =
		fit === 'compact' && selectedOptions.length > 0 ? '' : formatMessage(i18n.lastUpdated);

	return (
		<DateFilterWithCustomRangePicker
			contentFit={fit}
			options={options}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			label={label}
			onDropdownOpen={loadInitialOptions}
			selectedOptions={selectedOptions}
			customDateRange={customValue || { from: '', to: '' }}
			testId={testId}
			isFilterInDropdown={isFilterInDropdown}
			iconBefore={
				<FilterProductIcon
					showPrimaryProductIcon={showPrimaryProductIcon}
					primaryProduct={primaryProduct}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
		/>
	);
};

export default LastUpdatedFilter;
